import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { CheckCircle, XCircle, DownloadSimple } from "@phosphor-icons/react";

function typedSignatureDataUrl(name) {
  const c = document.createElement("canvas");
  c.width = 560; c.height = 160;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#0f172a";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  let size = 56;
  ctx.font = `italic ${size}px "Brush Script MT", "Segoe Script", "Snell Roundhand", cursive`;
  while (size > 22 && ctx.measureText(name).width > 500) {
    size -= 4;
    ctx.font = `italic ${size}px "Brush Script MT", "Segoe Script", "Snell Roundhand", cursive`;
  }
  ctx.fillText(name, 280, 78);
  ctx.strokeStyle = "#94a3b8";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(60, 125); ctx.lineTo(500, 125); ctx.stroke();
  return c.toDataURL("image/png");
}

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const point = (e) => {
    const c = ref.current;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ref.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(ref.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange("");
  };
  return (
    <div>
      <canvas ref={ref} width={560} height={160}
        data-testid="signature-pad"
        onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
        className="w-full h-40 border-2 border-dashed border-slate-300 rounded-md bg-slate-50 touch-none cursor-crosshair" />
      <div className="flex items-center justify-between mt-1">
        <span className={`text-xs ${hasInk ? "text-emerald-600 font-semibold" : "text-slate-400"}`}>
          {hasInk ? "Signed" : "Sign inside the box with your finger or mouse"}
        </span>
        <button type="button" data-testid="signature-clear" onClick={clear}
          className="text-xs font-semibold text-slate-500 hover:text-slate-800">Clear</button>
      </div>
    </div>
  );
}

export default function SupervisorApproval() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [reason, setReason] = useState("");
  const [signature, setSignature] = useState("");
  const [sigMode, setSigMode] = useState("draw");
  const [typedName, setTypedName] = useState("");
  const [done, setDone] = useState("");
  const [revisit, setRevisit] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/supervisor/token/${token}`)
      .then((r) => {
        if (r.data.already === "approved") {
          setRevisit(true);
          setDone("approved");
          setState({ loading: false });
        } else {
          setState({ loading: false, ts: r.data.timesheet });
        }
      })
      .catch((e) => setState({ loading: false, error: e.response?.data?.detail || "Link invalid or expired." }));
  }, [token]);

  const needSig = !!state.ts?._project?.require_signature;

  const act = async (action) => {
    setErr("");
    if (action === "reject" && !reason.trim()) { setErr("Reason is required to reject."); return; }
    if (action === "approve" && needSig && !signature) { setErr("Please sign the box before approving."); return; }
    try {
      const { data } = await api.post(`/supervisor/token/${token}/action`, {
        action,
        reason: action === "reject" ? reason : undefined,
        signature: action === "approve" && needSig ? signature : undefined,
      });
      setDone(data.status);
    } catch (e) { setErr(e.response?.data?.detail?.toString() || e.message); }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-100">
      <div className="max-w-md bg-white p-8 rounded-md border border-slate-200 text-center">
        <div className="tinylabel">Approval link</div><h1 className="text-2xl font-black mt-2">Unavailable</h1>
        <p className="text-slate-500 mt-3">{state.error}</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-100">
      <div className="max-w-md bg-white p-8 rounded-md border border-slate-200 text-center" data-testid="supervisor-done">
        {done === "approved" ? <CheckCircle size={64} className="mx-auto text-emerald-600" weight="fill" />
          : <XCircle size={64} className="mx-auto text-red-600" weight="fill" />}
        <h1 className="text-2xl font-black mt-4">Timesheet {done.toUpperCase()}</h1>
        <p className="text-slate-500 mt-2">
          {revisit
            ? "This timesheet was already approved. You can still save a copy below."
            : "Thanks — the contractor has been notified. You can close this tab."}
        </p>
        {done === "approved" && (
          <a href={`${API_BASE}/supervisor/token/${token}/pdf`}
            data-testid="supervisor-download-pdf"
            className="mt-6 w-full h-12 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold transition-colors">
            <DownloadSimple size={20} weight="bold" /> Download a copy for your records (PDF)
          </a>
        )}
      </div>
    </div>
  );

  const t = state.ts;
  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex flex-col items-center">
          <img src={`${API_BASE}/settings/company/logo`} alt="SOS" className="h-16 w-16 rounded-md object-contain" />
          <div className="font-black text-2xl tracking-tight mt-2">Safety Onsite Solutions</div>
          <div className="tinylabel mt-1">Timesheet approval</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="tinylabel">Worker</div><div className="font-semibold">{t._worker?.name}</div></div>
            <div><div className="tinylabel">Client</div><div className="font-semibold">{t._client?.name}</div></div>
            <div><div className="tinylabel">Project</div><div className="font-semibold">{t._project?.name}</div></div>
            <div><div className="tinylabel">PO #</div><div className="font-mono">{t._project?.po_number}</div></div>
            <div><div className="tinylabel">Location</div><div>{t._project?.location}</div></div>
            <div><div className="tinylabel">Week starting</div><div>{t.week_start}</div></div>
          </div>
          <div className="mt-6 grid grid-cols-7 gap-2 text-center">
            {["mon","tue","wed","thu","fri","sat","sun"].map((d) => (
              <div key={d} className="p-3 border border-slate-200 rounded-md">
                <div className="tinylabel">{d}</div>
                <div className="text-2xl font-black font-mono mt-1">{t.hours?.[d] ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="border border-slate-200 rounded-md p-4"><div className="tinylabel">Total hours</div><div className="text-2xl font-black font-mono text-green-600">{t.total_hours}</div></div>
            <div className="border border-slate-200 rounded-md p-4"><div className="tinylabel">Per-diem days</div><div className="text-2xl font-black font-mono">{t.per_diem_days}</div></div>
          </div>
          {t.exceptions?.length > 0 && (
            <div className="mt-4">
              <div className="tinylabel mb-2">Exceptions</div>
              {t.exceptions.map((e, i) => <div key={i} className="text-sm border-b border-slate-100 py-2 flex justify-between"><span>{e.description}</span><span className="font-mono">${e.amount}</span></div>)}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 mt-4">
          {needSig && (
            <div className="mb-5" data-testid="signature-section">
              <label className="tinylabel">Signature (required to approve)</label>
              <p className="text-sm text-slate-600 mt-1 mb-2 font-medium">
                By signing below, you are confirming the accuracy of this timesheet.
              </p>
              <div className="flex gap-2 mb-2">
                <button type="button" data-testid="sig-mode-draw"
                  onClick={() => { setSigMode("draw"); setSignature(""); setTypedName(""); }}
                  className={`h-9 px-4 rounded-md text-sm font-semibold border ${sigMode === "draw" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                  Draw
                </button>
                <button type="button" data-testid="sig-mode-type"
                  onClick={() => { setSigMode("type"); setSignature(""); setTypedName(""); }}
                  className={`h-9 px-4 rounded-md text-sm font-semibold border ${sigMode === "type" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                  Type my name
                </button>
              </div>
              {sigMode === "draw" ? (
                <SignaturePad onChange={setSignature} />
              ) : (
                <div>
                  <input value={typedName} data-testid="sig-typed-input"
                    onChange={(e) => {
                      const v = e.target.value;
                      setTypedName(v);
                      setSignature(v.trim() ? typedSignatureDataUrl(v.trim()) : "");
                    }}
                    placeholder="Type your full name"
                    className="w-full h-14 px-3 border border-slate-300 rounded-md text-base" />
                  {typedName.trim() ? (
                    <div className="mt-2 border-2 border-dashed border-slate-300 rounded-md bg-slate-50 p-2">
                      <img src={signature} alt="Typed signature preview" data-testid="sig-typed-preview" className="mx-auto max-h-24" />
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-400">Your name will appear as a signature.</div>
                  )}
                </div>
              )}
            </div>
          )}
          <label className="tinylabel">Reason (required if rejecting)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="supervisor-reason" rows={3}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" placeholder="Explain any issue with the reported hours or per-diem…"/>
          {err && <div className="text-red-600 text-sm mt-2">{err}</div>}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button data-testid="supervisor-reject" onClick={() => act("reject")}
              className="h-14 border-2 border-red-600 text-red-700 hover:bg-red-50 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"><XCircle size={22} weight="bold" /> Reject</button>
            <button data-testid="supervisor-approve" onClick={() => act("approve")}
              disabled={needSig && !signature}
              className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"><CheckCircle size={22} weight="bold" /> Approve</button>
          </div>
        </div>
      </div>
    </div>
  );
}
